import type {
  FeedbackRTValue,
  TestGroupingGroup,
  TestGroupingItem,
} from '@vedalib/editor/lib/elements'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import { rtValueToText, type RichTextValue } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'
import * as R from 'ramda'
import { useState, useEffect, useCallback, useRef } from 'react'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { setQuestion } from 'services/Store/Project/actions'
import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { shuffle } from 'utils/shuffle'

import TestFeedback from '../../courseKit/TestFeedback'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import { getFeedbackValue } from '../../utils/getFeedbackValue'
import TestGroupingContext from './TestGroupingContext'
import * as s from './TestGroupingElement.module.scss'
import DefaultElement from './components/DefaultView/DefaultElement'
import MobileElement from './components/MobileView/MobileElement'
import PdfAnswerCard from './components/PdfView/PdfAnswerCard'
import PdfGroupCard from './components/PdfView/PdfGroupCard'
import { createNewAnswer, createNewGroup, getCardsData } from './helper'
import type { TestGroupingElementType } from './testGroupingTypes'
import type { ValidationMap } from './types'

const TestGroupingElement: TestGroupingElementType = ({
  element: { id: elementId, value },
  styles,
  mode,
  font,
  feedback,
  block,
  onChange,
  state,
  setState,
}) => {
  const items = value.items as TestGroupingGroup[]
  const isEditor = mode.previewMode === PreviewMode.editor
  const blockState = useProjectContext(getBlockState, block?.uuid)
  const result = blockState?.result
  const initialSavedDestination = state?.destination
  const initialSavedTestResult = state?.testResult

  const [shuffledAnswers, setShuffledAnswers] = useState<TestGroupingItem[]>([])
  const [initialShuffledAnswers, setInitialShuffledAnswers] = useState<TestGroupingItem[]>([])
  const [validationMap, setValidationMap] = useState<ValidationMap>({})
  const [testResult, setTestResult] = useState<{ [key: string]: boolean }>(() => {
    if (result && initialSavedTestResult && !isEditor) {
      return initialSavedTestResult
    }
    return {}
  })
  const [stackIndexes, setStackIndexes] = useState<{ [groupValue: string]: number }>({})
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined)
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | undefined>(undefined)
  const [destination, setDestination] = useState<TestGroupingGroup[]>(() => {
    if (result && initialSavedDestination && !isEditor) {
      return initialSavedDestination
    }
    const { dest } = getCardsData(items)
    return dest
  })
  const destinationRef = useRef(destination)
  destinationRef.current = destination
  const { generalFbValid, activeGroup, activeAnswer, startOver, answersOrder } = state || {}

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })
  const rootStyles = useElementRootStyles(styles.root)
  const feedbackValue = getFeedbackValue(value)
  const isPdf = mode.previewMode === PreviewMode.pdf
  const isMobile = mode.deviceMode === DeviceMode.mobile
  const prevResultRef = useRef(result)
  const dispatch = useProjectDispatch()

  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testGrouping.type,
      elementId,
    },
  )

  const onChangeLabel = useCallback(
    (label: RichTextValue, path: (string | number)[]) => {
      onChange?.(R.assocPath(path, label, value))
    },
    [onChange, value],
  )

  const handleAddGroup = (groupValue: string, position: 'before' | 'after') => {
    const groupIndex = items.findIndex((g) => g.value === groupValue)
    const newItems = [...items]
    newItems.splice(
      position === 'before' ? groupIndex : groupIndex + 1,
      0,
      createNewGroup({ index: items.length + 1 }),
    )
    onChange?.({ ...value, items: newItems })
    setState?.({ startOver: true })
  }

  const handleAddAnswer = (groupValue: string) => {
    const newAnswer = createNewAnswer({
      index: (items.find((g) => g.value === groupValue)?.items?.length || 0) + 1,
    })
    const updatedItems = items.map((group) =>
      group.value === groupValue
        ? {
            ...group,
            items: [newAnswer, ...(group.items || [])],
          }
        : group,
    )
    onChange?.({ ...value, items: updatedItems })
    setState?.({ answersOrder: [newAnswer.value] })
  }

  const handleDeleteAnswer = (groupValue: string, answerValue: string) => {
    const updatedItems = items.map((group) =>
      group.value === groupValue
        ? { ...group, items: (group.items || []).filter((item) => item.value !== answerValue) }
        : group,
    )
    onChange?.({ ...value, items: updatedItems })
  }

  const handleDuplicateAnswer = (groupValue: string, answerValue: string) => {
    let newAnswerId: string | undefined
    const updatedItems = items.map((group) => {
      if (group.value !== groupValue) {
        return group
      }
      const answerIndex = (group.items || []).findIndex((item) => item.value === answerValue)
      const answer = group.items?.[answerIndex]
      if (!answer || answerIndex === -1) {
        return group
      }
      newAnswerId = generateId()
      const newItems = [...(group.items || [])]
      newItems.splice(answerIndex + 1, 0, { ...answer, value: newAnswerId })
      return { ...group, items: newItems }
    })
    onChange?.({ ...value, items: updatedItems })
  }

  const handleDeleteGroup = (groupValue: string) => {
    onChange?.({ ...value, items: items.filter((g) => g.value !== groupValue) })
  }

  const handleDuplicateGroup = (groupValue: string) => {
    const index = items.findIndex((g) => g.value === groupValue)
    const selectedGroup = items[index]
    const updatedItems = [...items]
    updatedItems.splice(index + 1, 0, { ...selectedGroup, value: generateId() })
    onChange?.({ ...value, items: updatedItems })
  }

  const handleChangeGeneralFeedback = useCallback(
    (newFeedbackValue: FeedbackRTValue) => {
      onChange?.({ ...value, generalFeedback: newFeedbackValue })
    },
    [onChange, value],
  )

  const handleClickOutside = useCallback(() => {
    setStackIndexes({})
    setSelectedGroupId(undefined)
  }, [])

  useEffect(() => {
    if (block?.uuid) {
      const allAnswersPlaced = shuffledAnswers.length === 0
      dispatch(
        setQuestion({
          elementId,
          value: { destination, testResult },
          isReady: allAnswersPlaced,
          blockId: block.uuid,
        }),
      )
    }
  }, [destination, testResult, block?.uuid, elementId, dispatch, shuffledAnswers.length])

  const contextValue = {
    destination,
    styles,
    mode,
    blockResult: result,
    feedback,
    shuffledAnswers,
    initialShuffledAnswers,
    testResult,
    validationMap,
    font,
    value,
    stackIndexes,
    selectedGroupId,
    selectedAnswerId,
    elementId,
    isActiveElement,
    activeIndex,
    state,
    onLabelSelect,
    onChangeLabel,
    isFill,
    onAddGroup: handleAddGroup,
    onAddAnswer: handleAddAnswer,
    onDeleteAnswer: handleDeleteAnswer,
    onDuplicateAnswer: handleDuplicateAnswer,
    onDeleteGroup: handleDeleteGroup,
    onDuplicateGroup: handleDuplicateGroup,
    onClickOutside: handleClickOutside,
    setDestination,
    setShuffledAnswers,
    setTestResult,
    setValidationMap,
    setStackIndexes,
    setSelectedGroupId,
    setSelectedAnswerId,
    setState,
    // xAPI analytics
    entityAnalyticsConfig,
    sendXapiData,
    baseObjectExtensions,
  }

  const prevItemsLengthRef = useRef<number | null>(null)
  const initializedFromResult = useRef(!!(result && initialSavedDestination && !isEditor))

  useEffect(() => {
    const { source, validationMap } = getCardsData(items)
    const sourceMap = new Map(source.map((item) => [item.value, item]))
    const isInitial = prevItemsLengthRef.current === null
    prevItemsLengthRef.current = source.length
    setValidationMap(validationMap)

    if (initializedFromResult.current) {
      if (shuffledAnswers.length > 0) {
        setShuffledAnswers([])
      }
      return
    }

    setDestination((prevDestination) => {
      const destinationMap = new Map(
        prevDestination.map((group) => [group.value, group.items || []]),
      )
      return items.map((group) => ({
        ...group,
        items: destinationMap.get(group.value) || [],
      }))
    })
    setShuffledAnswers((prev) => {
      if (isInitial || prev.length === 0) {
        return isEditor ? [...source].reverse() : shuffle(source)
      }

      const existingValues = new Set(prev.map((item) => item.value))
      const newAnswers = source.filter((item) => !existingValues.has(item.value))
      let updatedPrev = prev
        .map((item) => sourceMap.get(item.value) || item)
        .filter((item) => sourceMap.has(item.value))

      if (isEditor && newAnswers.length > 0) {
        const orderedNew = answersOrder
          ? [
              ...newAnswers.filter((a) => answersOrder.includes(a.value)),
              ...newAnswers.filter((a) => !answersOrder.includes(a.value)),
            ]
          : newAnswers
        return [...orderedNew, ...updatedPrev]
      }

      if (isEditor && answersOrder && answersOrder.length > 0 && newAnswers.length === 0) {
        const answerToMove = updatedPrev.find((a) => answersOrder.includes(a.value))
        if (answerToMove) {
          updatedPrev = [answerToMove, ...updatedPrev.filter((a) => a.value !== answerToMove.value)]
        }
      }

      return updatedPrev
    })

    setInitialShuffledAnswers((prev) => {
      if (isInitial || prev.length === 0) {
        return isEditor ? [...source].reverse() : shuffle(source)
      }

      const existingValues = new Set(prev.map((item) => item.value))
      const newAnswers = source.filter((item) => !existingValues.has(item.value))
      let updatedPrev = prev.map((item) => sourceMap.get(item.value) || item)

      if (isEditor && newAnswers.length > 0) {
        const orderedNew = answersOrder
          ? [
              ...newAnswers.filter((a) => answersOrder.includes(a.value)),
              ...newAnswers.filter((a) => !answersOrder.includes(a.value)),
            ]
          : newAnswers
        return [...orderedNew, ...updatedPrev]
      }

      if (isEditor && answersOrder && answersOrder.length > 0 && newAnswers.length === 0) {
        const answerToMove = updatedPrev.find((a) => answersOrder.includes(a.value))
        if (answerToMove) {
          updatedPrev = [answerToMove, ...updatedPrev.filter((a) => a.value !== answerToMove.value)]
        }
      }

      return updatedPrev
    })

    if (answersOrder && answersOrder.length > 0) {
      setState?.({ answersOrder: undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isEditor, answersOrder, setState])

  useEffect(() => {
    if (activeGroup) {
      setSelectedGroupId(activeGroup)
      setSelectedAnswerId(undefined)
    } else if (activeAnswer) {
      setSelectedAnswerId(activeAnswer)
      setSelectedGroupId(undefined)
    }
  }, [activeGroup, activeAnswer])

  useEffect(() => {
    const wasReset = prevResultRef.current && !result
    prevResultRef.current = result

    if (startOver || wasReset) {
      const { source } = getCardsData(items)
      const shuffledSource = isEditor ? [...source].reverse() : shuffle(source)

      setTestResult({})
      setDestination((prev) => prev.map((group) => ({ ...group, items: [] })))
      setStackIndexes({})
      setShuffledAnswers(shuffledSource)
      setInitialShuffledAnswers(shuffledSource)

      if (startOver && destination.every((group) => (group.items?.length || 0) === 0)) {
        setState?.({ startOver: false })
      }
    }
  }, [startOver, result, items, isEditor, destination, setState])

  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!result) {
      return
    }
    const correctAnswers: string[] = []
    items.forEach((group) => {
      const groupLabel = rtValueToText(group.target.label)
      ;(group.items || []).forEach((item) => {
        correctAnswers.push(`${rtValueToText(item.label)} in ${groupLabel}`)
      })
    })

    const yourAnswers: string[] = []
    destination.forEach((group) => {
      const groupLabel = rtValueToText(group.target.label)
      ;(group.items || []).forEach((item) => {
        yourAnswers.push(`${rtValueToText(item.label)} in ${groupLabel}`)
      })
    })

    announce(
      buildTestAnnouncement(
        result.isValid,
        correctAnswers.length ? correctAnswers.join(', ') : undefined,
        yourAnswers.length ? yourAnswers.join(', ') : undefined,
      ),
    )
  }, [result])

  if (isPdf) {
    const totalAnswers = shuffledAnswers.length
    return (
      <div className={s.root} style={rootStyles}>
        <TestGroupingContext.Provider value={contextValue}>
          <div className={s.pdfGroup}>
            {shuffledAnswers.map((item, idx) => (
              <PdfAnswerCard index={idx + 1} item={item} key={item.value} total={totalAnswers} />
            ))}
          </div>
          <div className={s.pdfGroup} style={{ marginTop: styles.indents.marginTop }}>
            {destination.map((group) => (
              <PdfGroupCard group={group} key={group.value} />
            ))}
          </div>
        </TestGroupingContext.Provider>
      </div>
    )
  }

  return (
    <div className={s.root} style={rootStyles}>
      <TestGroupingContext.Provider value={contextValue}>
        {isMobile ? <MobileElement /> : <DefaultElement />}
      </TestGroupingContext.Provider>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={isEditor ? generalFbValid : result?.isValid}
        mode={mode}
        onChange={handleChangeGeneralFeedback}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: value.generalFeedback,
        }}
        showFeedback={isEditor ? value.displayFeedback : value.displayFeedback && !!result}
        styles={feedback}
      />
    </div>
  )
}

export default TestGroupingElement
